import React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from '@perxis-auto/ui-kit/lib/components/Grid';
import { Header as UIHeader } from '@perxis-auto/ui-kit/lib/components/Header';

import PAGES from '@/constants/pages';
import getCarDefaultDescription from '@/features/cars/helpers/getCarDescription';
import getCarDefaultStatus from '@/features/cars/helpers/getCarStatus';
import getCarDefaultTitle from '@/features/cars/helpers/getCarTitle';
import mapTagsToMarkers from '@/features/cars/helpers/mapTagsToMarkers';
import useCarData from '@/features/cars/hooks/useCarData';
import { useAppSelector } from '@/features/hooks';
import { rootSelectors } from '@/features/store';
import { IExtendedTag } from '@/interfaces/cars.interfaces';
import { useInjection } from '@/services/hooks';
import { INavigationService } from '@/services/NavigationService';
import ServiceTypes from '@/services/ServiceTypes';

import { CarHeaderProps } from './CarHeader.types';
import CarHeaderSkeleton from './CarHeaderSkeleton';

import styles from './CarHeader.module.scss';

function CarHeader({
  showTitle = true,
  getCarTitle = getCarDefaultTitle,
  getCarDescription = getCarDefaultDescription,
  getCarStatus = getCarDefaultStatus,
}: CarHeaderProps) {
  const { t } = useTranslation();
  const navigation = useInjection<INavigationService>(ServiceTypes.Navigation);
  const tagsEnable = useAppSelector(rootSelectors.config.tagsEnableSelector);

  const { data: extendedCarItem = null, isLoading } = useCarData();

  if (isLoading) {
    return <CarHeaderSkeleton showTitle={showTitle} />;
  }

  if (!extendedCarItem) {
    return null;
  }

  const title = getCarTitle(extendedCarItem);
  const description = getCarDescription(extendedCarItem);
  const status = getCarStatus(extendedCarItem);
  const tagsToDisplay: Array<Omit<IExtendedTag, 'collection_id'>> = [];

  if (status) {
    tagsToDisplay.push({
      id: '0',
      displayName: status.label.trim(),
      disabled: false,
    });
  }

  const markers = mapTagsToMarkers(tagsToDisplay);

  const hasCarInfo = showTitle && !!title;
  const hasStatus = tagsEnable && !!status;
  const hasMarkers = !tagsEnable && !!markers.length;

  return (
    <UIHeader className={styles.carHeader}>
      <Row align="middle" gutter={[24, 'vertical-sky']}>
        <Col
          xs={{ span: 12, order: 1 }}
          sm={{ span: 12, order: 1 }}
          md={{ span: hasCarInfo ? 6 : 12, order: 1 }}
        >
          <Row justify="start">
            <UIHeader.BackButton
              label={t('card.back')}
              onClick={() => navigation.push(PAGES.Cars)}
            />
          </Row>
        </Col>
        {hasCarInfo && (
          <Col
            xs={{ span: 24, order: 3 }}
            sm={{ span: 24, order: 3 }}
            md={{ span: 12, order: 2 }}
          >
            <Row justify="center">
              <UIHeader.CarInfo title={title} description={description} />
            </Row>
          </Col>
        )}
        <Col
          xs={{ span: 12, order: 2 }}
          sm={{ span: 12, order: 2 }}
          md={{ span: hasCarInfo ? 6 : 12, order: hasCarInfo ? 3 : 2 }}
        >
          <Row justify="end">
            {hasMarkers && <UIHeader.Markers markers={markers} />}
            {hasStatus && <UIHeader.Status {...status} />}
          </Row>
        </Col>
      </Row>
    </UIHeader>
  );
}

export default CarHeader;
